// Display helpers for the FedWatch grid and timeline.

/** Fed funds target ranges are always 25bp wide — a contract guarantee. */
export const TARGET_RANGE_WIDTH_BP = 25

/**
 * The lower bound in basis points, rendered as the range it describes:
 * `350 → "3.50–3.75%"`.
 *
 * The width is hardcoded rather than inferred from the neighbouring column,
 * because the grid is ragged: adjacent columns can be non-adjacent ranges, and
 * measuring the gap between them would invent a width no one quoted.
 */
export function formatTargetRange(bp: number): string {
  if (!Number.isFinite(bp)) return '—'
  const lower = (bp / 100).toFixed(2)
  const upper = ((bp + TARGET_RANGE_WIDTH_BP) / 100).toFixed(2)
  return `${lower}–${upper}%`
}

/** Just the lower bound, for compact contexts: `350 → "3.50%"`. */
export function formatTargetLower(bp: number): string {
  if (!Number.isFinite(bp)) return '—'
  return `${(bp / 100).toFixed(2)}%`
}

/**
 * A probability in 0..1 as a percentage.
 *
 * Exact zero prints `0.0%` — that is a real observation, a range the tree carries
 * but prices at nothing. A tiny non-zero prints `<0.1%` rather than rounding down
 * to `0.0%`, so a possibility the market is pricing never reads as impossible.
 */
export function formatProbability(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const pct = value * 100
  if (value > 0 && pct < 0.05) return '<0.1%'
  return `${pct.toFixed(1)}%`
}

/**
 * Parses the decimal-string probability the wire carries.
 *
 * Rejects anything outside 0..1. That is not in tension with never renormalising:
 * we refuse to *rescale* legitimate values, which is no reason to accept a `1.01`
 * that cannot mean anything.
 */
export function parseProbability(value: string | undefined): number | null {
  if (value === undefined || value === '') return null
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) return null
  return parsed
}

/** `cumulativeMoveBp` as a signed move: `-25 → "−25bp"`, `0 → "no change"`. */
export function formatMove(bp: number, noChangeLabel = 'No change'): string {
  if (!Number.isFinite(bp)) return '—'
  if (bp === 0) return noChangeLabel
  return `${bp > 0 ? '+' : '−'}${Math.abs(bp)}bp`
}
